import { random } from 'lodash';
import { createAsset } from '../../api/canvasRquest';
import { checkMeiyeAmount } from '../../api/server';
import { createNodeExecuteTask, queryNodeExecuteTask } from '../node/nodeService';
import type { NodeExecuteTaskResult } from '../node/type';
import { getTaskSpeed } from '../../store/useTaskSpeedStore';
import {
  bindMyIpRoleCardTask, getMyIpRoleCardData, getMyIpRoleCardTaskDetail, normalizeIpId,
} from './myIpService';
import { getMyIpDetailMedia } from './detailMedia';
import { getMyIpProfileDisplay } from './profileDisplay';
import type { MyIpItem } from './types';
import { toolsCountCost } from '../mcp/mcpService';

export const MY_IP_ROLE_CARD_API_NAME = 'image_weave_anything2img';
export const MY_IP_ROLE_CARD_EFFECT_ID = '495340';
export const MY_IP_ROLE_CARD_BUSINESS_TYPE = 'ip_role_card';
const MY_IP_ROLE_CARD_ASSET_TOOL_TYPE = 'gen_role';

const ROLE_CARD_POLL_INTERVAL_MS = 3000;
const ROLE_CARD_POLL_MAX_COUNT = 120;
const ROLE_CARD_HISTORY_PAGE_SIZE = 20;
const ROLE_CARD_HISTORY_MAX_PAGES = 20;
const ROLE_CARD_COST_ITEM_ID = 'ip-role-card';

export interface MyIpRoleCardCost {
  cost: number;
  discountCost?: number;
}

export interface MyIpRoleCardGenerationResult {
  /** 生成结果与查看历史都优先展示带品牌水印的 last_image_urls。 */
  url: string;
  /** 无品牌水印转存原图，用于生成品牌水印图；缺省时与 url 相同。 */
  originalUrl?: string;
  fileName: string;
  taskId: string;
}

type MyIpRoleCardTask = NodeExecuteTaskResult['tasks'][string];

interface MyIpRoleCardTaskRecord extends MyIpRoleCardTask {
  task_id?: string;
  node_id?: string;
  name?: string;
  room_id?: string;
  node_list?: Array<{ node_id?: string; name?: string; }>;
}

/** 后端任务容器的 key 可能是序号，优先使用任务对象内的真实 task_id。 */
function getMyIpRoleCardTaskId(entryTaskId: string, task: MyIpRoleCardTaskRecord): string {
  return task.task_id?.trim() || entryTaskId;
}

/** 按真实 task_id 读取任务，兼容任务列表 key 为序号的 nodeexecutequery 响应。 */
function findMyIpRoleCardTask(
  tasks: NodeExecuteTaskResult['tasks'] | undefined,
  taskId: string,
): MyIpRoleCardTaskRecord | undefined {
  const directTask = tasks?.[taskId] as MyIpRoleCardTaskRecord | undefined;
  if (directTask && (!directTask.task_id || directTask.task_id.trim() === taskId)) return directTask;
  return (Object.values(tasks ?? {}) as MyIpRoleCardTaskRecord[]).find((task) => task.task_id?.trim() === taskId);
}

/** 角色卡任务使用 IP 专属房间，生成、轮询和刷新恢复始终命中同一任务空间。 */
export function getMyIpRoleCardRoomId(ipId: string): string {
  return `ipid-${normalizeIpId(ipId)}`;
}

/** 将详情页展示的角色设定按固定顺序拼成效果 prompt，空字段不生成占位描述。 */
export function buildMyIpRoleCardPrompt(item: MyIpItem): string {
  const profile = getMyIpProfileDisplay(item.profile);
  const personality = profile.personality.join('、');
  const fields: Array<[string, string]> = [
    ['角色名称', item.name],
    ['角色简介', item.summary],
    ['身份', profile.identity],
    ['人设', profile.persona],
    ['性格', personality === profile.persona ? '' : personality],
    ['外形', profile.appearance],
    ['视觉风格', profile.visualStyle],
    ['语言风格', profile.speechStyle],
    ['背景故事', profile.backstory],
    ...profile.moreSettings.map((setting): [string, string] => [setting.key, setting.value]),
  ];
  return fields
    .map(([label, value]) => [label.trim(), value.trim()] as const)
    .filter(([label, value]) => Boolean(label && value))
    .map(([label, value]) => `${label}：${value}`)
    .join('\n');
}

/** 生成任务与询价共用同一份业务参数，避免展示价格与实际扣费规格不一致。 */
export function buildMyIpRoleCardTaskParameters(item: MyIpItem): Record<string, unknown> {
  const media = getMyIpDetailMedia(item);
  const mainVisualUrl = media.coverUrl?.trim();
  const threeViewUrl = media.threeViewUrls[0]?.trim();
  if (!mainVisualUrl || !threeViewUrl) throw new Error('IP role card source images missing');
  const prompt = buildMyIpRoleCardPrompt(item);
  if (!prompt) throw new Error('IP role card prompt missing');
  return {
    effect_id: MY_IP_ROLE_CARD_EFFECT_ID,
    prompt,
    image_list: [mainVisualUrl, threeViewUrl],
  };
}

/** 角色卡询价有折扣时按折扣价校验余额，避免会员仍被原价拦截。 */
export function getMyIpRoleCardEffectiveCost(cost: MyIpRoleCardCost): number {
  return typeof cost.discountCost === 'number' ? cost.discountCost : cost.cost;
}

/** 使用角色卡实际生成参数查询萝卜消耗，折扣价由服务端按当前用户权益返回。 */
export async function getMyIpRoleCardCost(
  ipId: string,
  signal?: AbortSignal,
): Promise<MyIpRoleCardCost> {
  const item = await getMyIpRoleCardData(ipId, signal);
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const roomId = getMyIpRoleCardRoomId(item.ipId);
  const parameters = buildMyIpRoleCardTaskParameters(item);
  const result = await toolsCountCost({
    room_id: roomId,
    task_speed: getTaskSpeed(),
    items: [{
      id: ROLE_CARD_COST_ITEM_ID,
      tool_name: MY_IP_ROLE_CARD_API_NAME,
      params: parameters,
    }],
  });
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const costItem = result.items.find((entry) => entry.id === ROLE_CARD_COST_ITEM_ID);
  if (!costItem || typeof costItem.cost !== 'number') throw new Error('IP role card cost unavailable');
  return {
    cost: costItem.cost,
    ...(typeof costItem.discount_cost === 'number' ? { discountCost: costItem.discount_cost } : {}),
  };
}

/** 支持关闭弹窗时中断等待，避免轮询定时器继续占用页面。 */
function waitForNextPoll(signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout>;
    /** 关闭弹窗时同时清除当前轮询定时器。 */
    const handleAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    /** 等待完成后移除 abort 监听，长任务不会逐轮累积闭包。 */
    const handleComplete = () => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    };
    timer = setTimeout(handleComplete, ROLE_CARD_POLL_INTERVAL_MS);
    signal?.addEventListener('abort', handleAbort, { once: true });
  });
}

/** 按真实 task_id 等待水印图；转存原图仍用于资产入库，避免提前展示无水印结果。 */
async function pollRoleCardImage(
  taskId: string,
  roomId: string,
  signal?: AbortSignal,
): Promise<Pick<MyIpRoleCardGenerationResult, 'url' | 'originalUrl'>> {
  for (let count = 0; count < ROLE_CARD_POLL_MAX_COUNT; count += 1) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    // eslint-disable-next-line no-await-in-loop
    const result = await queryNodeExecuteTask([taskId], roomId);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const task = findMyIpRoleCardTask(result.tasks, taskId);
    if (!task) throw new Error('IP role card task not found');
    const failedStep = task?.steps?.find((step) => step.state === 'FAIL');
    if (task?.state === 'FAIL' || task?.error_message || failedStep) {
      throw new Error(task?.error_message || failedStep?.error_message || failedStep?.fail_code || 'IP role card generation failed');
    }
    const originalUrl = pickMyIpRoleCardImageUrl(task);
    // 执行中可能已返回转存原图，需等水印图或任务终态后再打开结果弹窗。
    if (originalUrl && !isMyIpRoleCardTaskPending(task)) {
      const displayUrl = pickMyIpRoleCardHistoryDisplayUrl(task) ?? originalUrl;
      return { url: displayUrl, ...(originalUrl !== displayUrl ? { originalUrl } : {}) };
    }
    if (task.state && !isMyIpRoleCardTaskRunningState(task.state)) {
      throw new Error('IP role card task finished without an image');
    }
    if (!task.state && !(task.steps ?? []).some((step) => step.state !== 'SUCCESS' && step.state !== 'FAIL')) {
      throw new Error('IP role card task finished without an image');
    }
    // eslint-disable-next-line no-await-in-loop
    await waitForNextPoll(signal);
  }
  throw new Error('IP role card generation timed out');
}

/** 生成任务写入的 node_id 前缀，历史列表用它排除其他角色的任务。 */
function getMyIpRoleCardNodeIdPrefix(ipId: string): string {
  return `ip_role_card_${normalizeIpId(ipId)}_`;
}

/** 只保留当前 IP 的角色卡工具任务，避免列表混入其他角色或非角色卡内容。 */
function isCurrentIpRoleCardTask(task: MyIpRoleCardTaskRecord, ipId: string, roomId: string): boolean {
  const prefix = getMyIpRoleCardNodeIdPrefix(ipId);
  const nodeIds = [task.node_id, ...(task.node_list ?? []).map((node) => node.node_id)]
    .filter((id): id is string => typeof id === 'string' && Boolean(id));
  const toolNames = [task.name, ...(task.node_list ?? []).map((node) => node.name)]
    .filter((name): name is string => typeof name === 'string' && Boolean(name));
  if (toolNames.length && !toolNames.includes(MY_IP_ROLE_CARD_API_NAME)) return false;
  if (nodeIds.length) return nodeIds.some((id) => id.startsWith(prefix));
  if (task.room_id) return task.room_id === roomId;
  return true;
}

/** 每条任务只取一张转存原图，供资产入库使用；缺少转存地址时回退结果图。 */
function pickMyIpRoleCardImageUrl(task: MyIpRoleCardTask): string | undefined {
  const transferred = (task.initial_transferred_urls ?? []).find((url) => typeof url === 'string' && url.trim());
  if (transferred) return transferred.trim();
  const images = (task.last_image_urls ?? []).filter((url) => typeof url === 'string' && url.trim());
  return images[images.length - 1]?.trim();
}

/**
 * 生成结果与「查看角色卡」统一优先展示 last_image_urls（含水印），
 * createAsset 入库仍使用转存原图。
 *
 * @param {MyIpRoleCardTask} task
 * @returns {string | undefined}
 */
function pickMyIpRoleCardHistoryDisplayUrl(task: MyIpRoleCardTask): string | undefined {
  const watermarked = (task.last_image_urls ?? [])
    .filter((url): url is string => typeof url === 'string' && Boolean(url.trim()))
    .map((url) => url.trim());
  if (watermarked.length) return watermarked[watermarked.length - 1];
  return pickMyIpRoleCardImageUrl(task);
}

/** 仅将明确的执行中状态交给轮询，未知或已终态状态必须结束恢复流程。 */
function isMyIpRoleCardTaskRunningState(state?: string): boolean {
  return ['PENDING', 'RUNNING', 'QUEUED', 'WAITING', 'PROCESSING'].includes(String(state || '').toUpperCase());
}

/** 判断最新角色卡任务是否仍未进入成功或失败终态。 */
function isMyIpRoleCardTaskPending(task?: MyIpRoleCardTask): boolean {
  if (!task) return false;
  if (task.state === 'SUCCESS' || task.state === 'FAIL' || task.error_message) return false;
  if (task.last_image_urls?.some((url) => typeof url === 'string' && Boolean(url.trim()))) return false;
  if (task.state) return isMyIpRoleCardTaskRunningState(task.state);
  return (task.steps ?? []).some((step) => step.state !== 'SUCCESS' && step.state !== 'FAIL');
}

/** 按 IP 专属房间只读取最新进行中角色卡任务，避免刷新后重复创建生成任务。 */
async function getLatestMyIpRoleCardTask(
  ipId: string,
  signal?: AbortSignal,
): Promise<{ roomId: string; taskId: string; task: MyIpRoleCardTask; } | undefined> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const roomId = getMyIpRoleCardRoomId(ipId);
  const result = await queryNodeExecuteTask(
    undefined,
    roomId,
    undefined,
    [roomId],
    1,
    1,
    MY_IP_ROLE_CARD_BUSINESS_TYPE,
  );
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const [entryTaskId, task] = Object.entries(result.tasks ?? {}).find(([, entry]) => (
    isCurrentIpRoleCardTask(entry, ipId, roomId) && isMyIpRoleCardTaskPending(entry)
  )) ?? [];
  const taskId = task ? getMyIpRoleCardTaskId(entryTaskId, task) : '';
  if (!taskId || !task) return undefined;
  return { roomId, taskId, task };
}

/**
 * 读取最新已成功的角色卡任务。
 * 用户生成后离开页面时，客户端轮询中断不会走 createAsset；回页用此结果补登历史。
 *
 * @param {string} ipId
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ roomId: string; taskId: string; url: string; } | undefined>}
 */
async function getLatestCompletedMyIpRoleCardTask(
  ipId: string,
  signal?: AbortSignal,
): Promise<{ roomId: string; taskId: string; url: string; } | undefined> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const roomId = getMyIpRoleCardRoomId(ipId);
  const result = await queryNodeExecuteTask(
    undefined,
    roomId,
    undefined,
    [roomId],
    1,
    1,
    MY_IP_ROLE_CARD_BUSINESS_TYPE,
    1,
  );
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const [entryTaskId, task] = Object.entries(result.tasks ?? {}).find(([, entry]) => {
    if (entry.state === 'FAIL' || entry.error_message) return false;
    if (!isCurrentIpRoleCardTask(entry, ipId, roomId)) return false;
    return Boolean(pickMyIpRoleCardImageUrl(entry));
  }) ?? [];
  const taskId = task ? getMyIpRoleCardTaskId(entryTaskId, task) : '';
  if (!taskId || !task) return undefined;
  const url = pickMyIpRoleCardImageUrl(task);
  if (!url) return undefined;
  return { roomId, taskId, url };
}

/**
 * 创建任务后立即绑定 IP，离开页面后可用 detail 找回同一 task_id。
 * 绑定失败不阻断生成；可靠回写仍依赖 Agent 侧 update。
 *
 * @param {string} ipId
 * @param {string} taskId
 * @param {number} version
 * @param {AbortSignal} [signal]
 * @returns {Promise<void>}
 */
async function bindMyIpRoleCardTaskSafely(
  ipId: string,
  taskId: string,
  version: number,
  signal?: AbortSignal,
): Promise<void> {
  try {
    await bindMyIpRoleCardTask(ipId, taskId, version, signal);
  } catch (error) {
    if (signal?.aborted) throw error;
    console.warn('[myIpRoleCardGeneration] bind role card task failed', error);
  }
}

/** 生成完成后再次绑定任务，把执行结果写入 IP 保存状态，避免回页重复展示成功态。 */
async function saveCompletedMyIpRoleCardTask(
  ipId: string,
  taskId: string,
  version: number,
  signal?: AbortSignal,
): Promise<void> {
  let nextVersion = version;
  if (nextVersion < 1) {
    const item = await getMyIpRoleCardData(ipId, signal);
    nextVersion = item.version;
  }
  if (nextVersion >= 1) await bindMyIpRoleCardTaskSafely(ipId, taskId, nextVersion, signal);
}

/**
 * 读取已绑定任务的当前快照。
 * 执行中即使已有转存原图也继续等水印图；明确失败视为终态。
 */
async function getMyIpRoleCardTaskSnapshot(
  taskId: string,
  roomId: string,
  signal?: AbortSignal,
): Promise<{ status: 'pending' | 'completed' | 'failed' | 'missing'; url?: string; }> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const result = await queryNodeExecuteTask([taskId], roomId);
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const task = findMyIpRoleCardTask(result.tasks, taskId);
  if (!task) return { status: 'missing' };
  const failedStep = task.steps?.find((step) => step.state === 'FAIL');
  if (task.state === 'FAIL' || task.error_message || failedStep) return { status: 'failed' };
  if (isMyIpRoleCardTaskPending(task)) return { status: 'pending' };
  const url = pickMyIpRoleCardImageUrl(task);
  if (url) return { status: 'completed', url };
  if (task.state && !isMyIpRoleCardTaskRunningState(task.state)) return { status: 'failed' };
  if (!task.state && !(task.steps ?? []).some((step) => step.state !== 'SUCCESS' && step.state !== 'FAIL')) {
    return { status: 'failed' };
  }
  return { status: 'pending' };
}

/**
 * 进入 IP 详情时优先按服务端绑定关系恢复：未保存则按 task_id 查询并回写，已保存只保留历史资产。
 * 仅当本会话接续到「进行中任务完成」时才返回结果供弹窗；进入时已成功的任务只补登，避免每次进详情重复弹出。
 *
 * @param {string} ipId
 * @param {string} [fileName]
 * @param {AbortSignal} [signal]
 * @returns {Promise<MyIpRoleCardGenerationResult | undefined>}
 */
export async function resumeMyIpRoleCardImage(
  ipId: string,
  fileName?: string,
  signal?: AbortSignal,
): Promise<MyIpRoleCardGenerationResult | undefined> {
  const resultFileName = fileName?.trim() || 'ip-role-card';
  const roomId = getMyIpRoleCardRoomId(ipId);

  try {
    const taskDetail = await getMyIpRoleCardTaskDetail(ipId, undefined, signal);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (taskDetail.saved) {
      // 已保存任务只恢复状态；历史资产在生成完成时登记，读取详情不能再次创建。
      return undefined;
    }
    const boundTaskId = taskDetail.taskId || taskDetail.currentTaskId;
    if (boundTaskId) {
      // saved=false 只表示尚无保存凭据，向执行侧查询生成状态。
      const snapshot = await getMyIpRoleCardTaskSnapshot(boundTaskId, roomId, signal);
      if (snapshot.status === 'completed' && snapshot.url) {
        await saveCompletedMyIpRoleCardTask(ipId, boundTaskId, taskDetail.version, signal);
        saveMyIpRoleCardToHistory(snapshot.url, resultFileName, boundTaskId, roomId).catch(() => undefined);
        // 进入详情时任务已结束：只回写凭据，不自动弹窗。
        return undefined;
      }
      if (snapshot.status === 'pending') {
        const image = await pollRoleCardImage(boundTaskId, roomId, signal);
        await saveCompletedMyIpRoleCardTask(ipId, boundTaskId, taskDetail.version, signal);
        saveMyIpRoleCardToHistory(image.originalUrl ?? image.url, resultFileName, boundTaskId, roomId).catch(() => undefined);
        return { ...image, fileName: resultFileName, taskId: boundTaskId };
      }
      // missing / failed：继续走房间任务列表兜底。
    }
  } catch (error) {
    if (signal?.aborted) throw error;
    // detail 不可用时回退到房间任务列表，兼容尚未部署绑定接口的环境。
    console.warn('[myIpRoleCardGeneration] role card task detail unavailable', error);
  }

  const pendingTask = await getLatestMyIpRoleCardTask(ipId, signal);
  if (pendingTask) {
    const image = await pollRoleCardImage(pendingTask.taskId, pendingTask.roomId, signal);
    await saveCompletedMyIpRoleCardTask(ipId, pendingTask.taskId, 0, signal);
    saveMyIpRoleCardToHistory(image.originalUrl ?? image.url, resultFileName, pendingTask.taskId, pendingTask.roomId).catch(() => undefined);
    return { ...image, fileName: resultFileName, taskId: pendingTask.taskId };
  }
  // 生成后离开页面：服务端已成功但浏览器未执行到 createAsset；回页只补登，不重复弹出。
  const completedTask = await getLatestCompletedMyIpRoleCardTask(ipId, signal);
  if (!completedTask) return undefined;
  await saveCompletedMyIpRoleCardTask(ipId, completedTask.taskId, 0, signal);
  saveMyIpRoleCardToHistory(
    completedTask.url,
    resultFileName,
    completedTask.taskId,
    completedTask.roomId,
  ).catch(() => undefined);
  return undefined;
}

/**
 * 按当前 IP 的专属任务房间读取全部角色卡结果，不会创建新的生成任务或历史资产。
 *
 * @param {string} ipId
 * @param {string} [fileName]
 * @param {AbortSignal} [signal]
 * @returns {Promise<MyIpRoleCardGenerationResult[]>}
 */
export async function getMyIpRoleCardHistory(
  ipId: string,
  fileName?: string,
  signal?: AbortSignal,
): Promise<MyIpRoleCardGenerationResult[]> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const roomId = getMyIpRoleCardRoomId(ipId);
  const resultFileName = fileName?.trim() || 'ip-role-card';
  const results: MyIpRoleCardGenerationResult[] = [];
  const seen = new Set<string>();

  for (let page = 1; page <= ROLE_CARD_HISTORY_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await queryNodeExecuteTask(
      undefined,
      roomId,
      undefined,
      [roomId],
      page,
      ROLE_CARD_HISTORY_PAGE_SIZE,
      MY_IP_ROLE_CARD_BUSINESS_TYPE,
      1,
    );
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const entries = Object.entries(result.tasks ?? {});
    entries.forEach(([entryTaskId, task]) => {
      if (task.state === 'FAIL' || task.error_message) return;
      if (!isCurrentIpRoleCardTask(task, ipId, roomId)) return;
      const displayUrl = pickMyIpRoleCardHistoryDisplayUrl(task);
      if (!displayUrl) return;
      const taskId = getMyIpRoleCardTaskId(entryTaskId, task);
      // 任务列表可能同时用序号 key 与真实 task_id 暴露同一任务，按 taskId 去重。
      if (seen.has(taskId)) return;
      seen.add(taskId);
      const assetUrl = pickMyIpRoleCardImageUrl(task) ?? displayUrl;
      results.push({
        url: displayUrl,
        ...(assetUrl !== displayUrl ? { originalUrl: assetUrl } : {}),
        fileName: resultFileName,
        taskId,
      });
    });
    if (entries.length < ROLE_CARD_HISTORY_PAGE_SIZE) break;
  }
  return results;
}

/** 将 IP 角色卡生成结果自动登记到全站历史记录，入库失败不阻塞结果弹窗。 */
async function saveMyIpRoleCardToHistory(
  url: string,
  fileName: string,
  taskId: string,
  roomId: string,
): Promise<void> {
  try {
    await createAsset({
      task_type: 'tool',
      tool_type: MY_IP_ROLE_CARD_ASSET_TOOL_TYPE,
      material_type: 'image',
      source_id: taskId,
      task_id: taskId,
      source_scene: 'task_generate',
      url,
      thumbnail_url: url,
      ...(fileName ? { name: fileName } : {}),
      ...(roomId ? { room_id: roomId } : {}),
    });
  } catch (error) {
    console.warn('[myIpRoleCardGeneration] save history asset failed', error);
  }
}

/**
 * 使用 IP 专属任务房间提交角色卡主图与三视图效果任务。
 * 创建任务后立即绑定 IP，再轮询结果；可靠凭据回写仍由 Agent update 完成。
 */
export async function generateMyIpRoleCardImage(
  ipId: string,
  signal?: AbortSignal,
): Promise<MyIpRoleCardGenerationResult> {
  const item = await getMyIpRoleCardData(ipId, signal);
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const roomId = getMyIpRoleCardRoomId(item.ipId);
  const parameters = buildMyIpRoleCardTaskParameters(item);
  const [amountResult, costResult] = await Promise.all([
    checkMeiyeAmount(),
    toolsCountCost({
      room_id: roomId,
      task_speed: getTaskSpeed(),
      items: [{
        id: ROLE_CARD_COST_ITEM_ID,
        tool_name: MY_IP_ROLE_CARD_API_NAME,
        params: parameters,
      }],
    }),
  ]);
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const costItem = costResult.items.find((entry) => entry.id === ROLE_CARD_COST_ITEM_ID);
  if (!costItem || typeof costItem.cost !== 'number') throw new Error('IP role card cost unavailable');
  const costInfo: MyIpRoleCardCost = {
    cost: costItem.cost,
    ...(typeof costItem.discount_cost === 'number' ? { discountCost: costItem.discount_cost } : {}),
  };
  if (Number(amountResult.amount) < getMyIpRoleCardEffectiveCost(costInfo)) {
    throw new Error('insufficient_credits');
  }
  const nodeId = `ip_role_card_${item.ipId}_${Date.now()}`;
  const taskResult = await createNodeExecuteTask({
    room_id: roomId,
    params: {
      name: MY_IP_ROLE_CARD_API_NAME,
      tool_abstract_name: { cn: '生成IP角色卡', en: '' },
      node_id: nodeId,
      parameters: {
        ...parameters,
        random: `${Date.now()}-${random(10000000, 99999999)}`,
      },
    },
    workflow_version: 'v2',
    business_type: MY_IP_ROLE_CARD_BUSINESS_TYPE,
    taskSpeed: getTaskSpeed(),
  });
  const [task] = Array.isArray(taskResult) ? taskResult : [taskResult];
  if (!task?.task_id) throw new Error('IP role card task creation failed');
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  // 拿到 task_id 后立刻绑定，避免离开页面后无法按 IP 找回同一任务。
  if (item.version >= 1) {
    await bindMyIpRoleCardTaskSafely(item.ipId, task.task_id, item.version, signal);
  }
  const image = await pollRoleCardImage(task.task_id, roomId, signal);
  await saveCompletedMyIpRoleCardTask(item.ipId, task.task_id, item.version, signal);
  // 历史入库与结果展示解耦；即使资产服务短暂失败，也要保留本次生成结果。
  saveMyIpRoleCardToHistory(
    image.originalUrl ?? image.url,
    item.name.trim() || 'ip-role-card',
    task.task_id,
    roomId,
  ).catch(() => undefined);
  return {
    ...image,
    fileName: item.name.trim() || 'ip-role-card',
    taskId: task.task_id,
  };
}
